import React, { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  TextField,
  IconButton,
  Button,
  Alert,
  Link,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const PhoneSettings = ({ userData, setSnackbar }) => {
  const [phoneNotifications, setPhoneNotifications] = useState([""]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (
      userData?.leadNotifications &&
      Array.isArray(userData.leadNotifications)
    ) {
      setPhoneNotifications(
        userData.leadNotifications.length > 0
          ? userData.leadNotifications
          : [""]
      );
    }
  }, [userData]);

  const handlePhoneAdd = () => {
    if (phoneNotifications.length < 5)
      setPhoneNotifications([...phoneNotifications, ""]);
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...phoneNotifications];
    newPhones[index] = value;
    setPhoneNotifications(newPhones);
  };

  const handlePhoneRemove = (index) => {
    if (phoneNotifications.length > 1) {
      setPhoneNotifications(phoneNotifications.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validPhones = phoneNotifications.filter((phone) => phone.trim());

    if (validPhones.length === 0) {
      setSnackbar({
        open: true,
        message: "Please add at least one phone number",
        severity: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINT}/user/assign-leads-phones`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({ phones: validPhones }),
      });

      if (!response.ok) throw new Error();
      setSnackbar({
        open: true,
        message: "Phone numbers updated successfully!",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to update phone numbers.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Typography variant="h6" fontWeight="bold">
          Phone Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage phone numbers for lead notifications
        </Typography>

        {phoneNotifications.map((phone, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              type="tel"
              placeholder="Enter phone number (e.g., +1234567890)"
              value={phone}
              onChange={(e) => handlePhoneChange(index, e.target.value)}
            />
            {phoneNotifications.length > 1 && (
              <IconButton
                color="error"
                onClick={() => handlePhoneRemove(index)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}

        {phoneNotifications.length < 5 ? (
          <Button
            onClick={handlePhoneAdd}
            startIcon={<AddIcon />}
            size="small"
            sx={{ alignSelf: "flex-start" }}
          >
            Add Number
          </Button>
        ) : (
          <Alert severity="info">
            Maximum 5 numbers.{" "}
            <Link href="/home/help-feedback">Contact Admin</Link>
          </Alert>
        )}

        <Stack
          direction="row"
          sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            startIcon={<CheckIcon />}
            loading={submitting}
          >
            Save Changes
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};

export default PhoneSettings;
